/**
 * Context d'authentification pour gérer l'état de l'utilisateur connecté.
 */
import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/api';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      // SÉCURITÉ: Avec les cookies httpOnly, on essaie directement de récupérer l'utilisateur
      // Si les cookies sont présents, l'authentification se fera automatiquement
      try {
        const response = await authService.getCurrentUser();
        setUser(response.data);
      } catch (error) {
        // Si erreur 401, l'utilisateur n'est pas connecté (cookies expirés/invalides)
        // C'est normal, on ne log pas d'erreur
        if (error.response?.status !== 401) {
          logger.error('Erreur lors du chargement de l\'utilisateur:', error);
        }

        // Nettoyer complètement localStorage et les éventuels tokens invalides
        // Ceci résout le problème où d'anciens tokens (d'avant la correction DEBUG)
        // peuvent empêcher la reconnexion
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');

        setUser(null);
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      // Nettoyer d'abord tous les anciens tokens pour éviter les conflits
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      logger.log('Tentative de connexion...');
      const response = await authService.login(email, password);

      // Vérifier si le 2FA est requis
      if (response.data.requires_2fa) {
        logger.log('2FA requis pour cet utilisateur');
        return {
          success: true,
          requires_2fa: true,
          email: response.data.email,
          message: response.data.message
        };
      }

      // SÉCURITÉ: Avec les cookies httpOnly, les tokens sont automatiquement stockés
      // En développement, stocker aussi en localStorage pour contourner les problèmes de cookies cross-port
      logger.log('Connexion réussie, récupération du profil utilisateur...');

      // En développement, stocker les tokens depuis le body
      if (response.data.access && response.data.refresh) {
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        logger.log('Tokens stockés en localStorage (mode développement)');
      }

      // L'utilisateur est déjà dans response.data.user
      if (response.data.user) {
        logger.log('Profil utilisateur reçu:', response.data.user);
        setUser(response.data.user);
        return { success: true };
      }

      // Sinon on le récupère via l'API
      const userResponse = await authService.getCurrentUser();
      logger.log('Profil utilisateur reçu:', userResponse.data);
      setUser(userResponse.data);

      return { success: true };
    } catch (error) {
      logger.error('Erreur de connexion:', error);
      logger.error('Détails erreur:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.detail || error.message || 'Erreur de connexion',
      };
    }
  };

  const verify2FA = async (email, code) => {
    try {
      logger.log('Vérification du code 2FA...');
      const response = await authService.verify2FA(email, code);

      // SÉCURITÉ: Avec les cookies httpOnly, les tokens sont automatiquement stockés
      // En développement, stocker aussi en localStorage
      logger.log('Code 2FA validé, récupération du profil utilisateur...');

      // En développement, stocker les tokens depuis le body
      if (response.data.access && response.data.refresh) {
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        logger.log('Tokens stockés en localStorage (mode développement)');
      }

      // L'utilisateur est déjà dans response.data.user
      if (response.data.user) {
        logger.log('Profil utilisateur reçu:', response.data.user);
        setUser(response.data.user);
        return { success: true };
      }

      // Sinon on le récupère via l'API
      const userResponse = await authService.getCurrentUser();
      logger.log('Profil utilisateur reçu:', userResponse.data);
      setUser(userResponse.data);

      return { success: true };
    } catch (error) {
      logger.error('Erreur de vérification 2FA:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.detail || error.message || 'Code invalide',
      };
    }
  };

  const resend2FA = async (email) => {
    try {
      logger.log('Renvoi du code 2FA...');
      await authService.resend2FA(email);
      return { success: true };
    } catch (error) {
      logger.error('Erreur de renvoi 2FA:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.detail || error.message || 'Erreur de renvoi',
      };
    }
  };

  const register = async (userData) => {
    try {
      await authService.register(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Erreur lors de l\'inscription',
      };
    }
  };

  const logout = async () => {
    try {
      // SÉCURITÉ: Appeler le backend pour supprimer les cookies httpOnly
      await authService.logout();
    } catch (error) {
      logger.error('Erreur lors de la déconnexion:', error);
      // On continue quand même la déconnexion côté client
    }

    // Supprimer les tokens de localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    logger.log('Tokens supprimés de localStorage');

    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }));
  };

  const value = {
    user,
    loading,
    login,
    verify2FA,
    resend2FA,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'ADMIN',
    isParent: user?.role === 'PARENT',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
